<template>
	<LayoutFlex
		class="orders-state-card"
		padding="var(--size-20) var(--orders-state-inline, var(--size-24))"
		gap="var(--size-16)"
		background="--background-white"
		corner-radius="var(--size-24)"
	>
		<LayoutFlex padding="0" gap="var(--size-8)" role="alert">
			<Text v-if="title" font="--text-18-22-med" color="--text-dark-primary">{{ title }}</Text>
			<Text font="--text-16-22-reg" :color="KOVA.textSecondary">{{ message }}</Text>
		</LayoutFlex>
		<!-- A row of its own: the button keeps its width in the column. -->
		<LayoutFlex v-if="actionLabel" padding="0" flex-direction="row">
			<Button ref="action" hierarchy="secondary" :loading="busy" @ds:click="emit('action')">
				<Text font="--text-16-18-semi">{{ actionLabel }}</Text>
			</Button>
		</LayoutFlex>
	</LayoutFlex>
</template>

<script setup lang="ts">
import { ref, type ComponentPublicInstance } from 'vue'
import { Button, LayoutFlex, Text } from '@simple-life-app/growth-ui-kova'
import { KOVA } from '@/designTokens'

/**
 * A state of the Orders list that stands in for data, or warns about it (spec §6; no Figma frame — the white card of
 * the list, the repo's error-card idiom from Status.vue): the list's first-load error and the stale notice after a
 * failed refresh. The details draw their own state cards (OrderDetails: the drawer's radius, the full-width stacked
 * buttons). Title and message are announced (`role="alert"`); the button stays outside the alert so it is not read
 * as part of the message. `action` is its one button ("Try again"); `busy` holds it disabled with the DS spinner
 * while what it started is in flight, so a second tap sends nothing.
 */

defineProps<{
	/** Optional — the stale notice is one sentence. */
	title?: string
	message: string
	actionLabel?: string
	busy?: boolean
}>()

const emit = defineEmits<{ action: [] }>()

const action = ref<ComponentPublicInstance | null>(null)

defineExpose({
	/** Give the focus back to the button — a browser takes it off a button as it turns disabled. */
	focusAction: () => (action.value?.$el as HTMLElement | undefined)?.focus({ preventScroll: true }),
})
</script>

<style scoped>
/* In line with the list's cards: 24 in from the sides on a phone, 32 once they are rows (OrderCard, "No orders
   yet"). */
@container orders (min-width: 560px) {
	.orders-state-card {
		--orders-state-inline: var(--size-32);
	}
}
</style>
